// AuthContext.tsx
import { CONTRIBUTOR_SESSION_STORAGE_KEY } from "@/constants";
import { createContext, useContext, useEffect, useState } from "react";

export type MicrosoftPerson = {
	persontype: "microsoft";
};

export type ContributorPerson = {
	email: string;
	username: string;
	accessToken: string;
	refreshToken: string;
	persontype: "contributor";
};

type Person = MicrosoftPerson | ContributorPerson;

type AuthContextType = {
	person: Person | null;
	loginWithAzure: (person: MicrosoftPerson) => void;
	loginWithEmail: (person: ContributorPerson) => void;
	logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const [person, setPerson] = useState<Person | null>(null);

	const loginWithAzure = (person: Person) => setPerson(person);
	const loginWithEmail = (person: Person) => setPerson(person);
	const logout = () => {
		setPerson(null);
		sessionStorage.removeItem(CONTRIBUTOR_SESSION_STORAGE_KEY);
	};

	useEffect(() => {
		// On mount, check if there's a contributor in session storage
		const storedContributor = sessionStorage.getItem(
			CONTRIBUTOR_SESSION_STORAGE_KEY
		);
		if (storedContributor) {
			setPerson(JSON.parse(storedContributor));
		}
	}, []);

	return (
		<AuthContext.Provider
			value={{ person, loginWithAzure, loginWithEmail, logout }}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error("useAuth must be used within AuthProvider");
	return ctx;
};
